import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';

import { projectService, type PlanningActionSelectionInput } from '../services/projectService';
import type { Task } from '../services/taskService';
import {
  planningActionStages, planningActionTaskRows, planningPropagationId,
  type PlanningActionScope, type PlanningActionTaskRow,
} from '../utils/planningActionTasks';

import { usePlanningTasks } from './PlanningTasks';

export interface PlanningSelectableTaskRow extends PlanningActionTaskRow {
  selectable: boolean;
  checked: boolean;
  indeterminate: boolean;
  selectionHint?: string;
}

interface SelectionUnit {
  key: string;
  stage: string;
  pageId?: string;
  taskId?: string;
  rowId: string;
}

type SelectionChoices = Readonly<Record<string, boolean>>;
interface PreparedSelection {
  id: string;
  payload: PlanningActionSelectionInput;
  label?: string;
  units: SelectionUnit[];
  baseline: Record<string, string>;
}
interface SelectionRecord {
  choices: SelectionChoices;
  prepared?: PreparedSelection;
  retired?: string[];
}
const records = new Map<string, SelectionRecord>();
const listeners = new Map<string, Set<() => void>>();
const previews = new Map<string, Promise<unknown>>();
const EMPTY_RECORD: SelectionRecord = { choices: {} };
const RUNNING = new Set(['queued', 'ai_in_progress', 'human_in_progress']);
const EXECUTING = new Set(['ai_in_progress', 'human_in_progress']);
const FINISHED = new Set(['done', 'invalid', 'manual_review']);
const rowStage = (row: PlanningActionTaskRow): string => (
  row.task?.planning_metadata?.stage ?? (row.id.startsWith('planned:') ? row.id.slice(8) : '')
);
const runnable = (row: PlanningActionTaskRow, includeQueued = false): boolean => (
  !row.task || ((!RUNNING.has(row.task.status) || (includeQueued && row.task.status === 'queued'))
    && !FINISHED.has(row.task.status)
    && row.task.planning_metadata?.artifact_type !== 'retry_attempt')
);

// Match backend Start eligibility; an agent rerun can target the same stages.
const initialStartGate = (scope: PlanningActionScope): boolean => Boolean(scope.buttons?.some((button) => {
  const agents = button.payload?.agents;

  return button.action === 'trigger_agents' && Array.isArray(agents) && agents.length === 1 && agents[0] === 'plan_all';
}));

const resumeGate = (scope: PlanningActionScope): boolean => {
  if (!scope.current || scope.followActive || scope.historyOnly || !scope.projectId) return false;
  const buttons = scope.buttons ?? [];

  if (buttons.some((button) => button.action === 'trigger_agents')) return initialStartGate(scope);
  const explicitResume = buttons.some((button) => (
    ['resume_design', 'run_artifact_updates'].includes(button.action) || ['continue', 'credit_continue'].includes(button.key)
  ));

  return explicitResume || (Boolean(scope.artifactType)
    && !buttons.some((button) => ['start_build', 'trigger_stage_deploy'].includes(button.action)))
    || (!buttons.length && Boolean(scope.phase));
};

const selectionUnits = (rows: PlanningActionTaskRow[], includeQueued = false): SelectionUnit[] => (
  Array.from(new Set(rows.map(rowStage).filter(Boolean))).flatMap((stage) => {
    const stageRows = rows.filter((row) => rowStage(row) === stage);
    const pages = stageRows.filter((row) => row.task?.planning_metadata?.artifact_type === 'design_page');
    const parent = stageRows.find((row) => !row.task || row.task.planning_metadata?.artifact_type === 'stage');

    if (stage === 'artifact_updates') return stageRows.filter((row) => row.task
      && row.task.planning_metadata?.artifact_type === 'propagation_update' && runnable(row, includeQueued))
      .map((row) => ({ key: `task:${row.id}`, stage, taskId: row.id, rowId: row.id }));
    if (pages.length) return pages.filter((row) => runnable(row, includeQueued)).map((row) => ({
      key: `page:${row.id}`, stage, pageId: row.id, rowId: row.id,
    }));

    return parent && runnable(parent, includeQueued) ? [{ key: `stage:${stage}`, stage, rowId: parent.id }] : [];
  })
);

const rowAliases = (row: PlanningActionTaskRow): string[] => (
  [row.id, row.task?.planning_key, row.task?.planning_metadata?.task_key]
    .filter((value): value is string => Boolean(value))
);

// Sections are indivisible page dependencies; following parents also handles nested sub-artifacts.
// eslint-disable-next-line max-statements
const pageAncestor = (row: PlanningActionTaskRow, rows: PlanningActionTaskRow[]): string | undefined => {
  const known = new Map(rows.flatMap((item) => rowAliases(item).map((key) => [key, item] as const)));
  const visited = new Set<string>();
  let current: PlanningActionTaskRow | undefined = row;

  while (current && !visited.has(current.id)) {
    if (current.task?.planning_metadata?.artifact_type === 'design_page') return current.id;
    visited.add(current.id);
    current = known.get(current.task?.planning_metadata?.parent_key ?? '');
  }

  return undefined;
};

const unitsForRow = (
  row: PlanningActionTaskRow, rows: PlanningActionTaskRow[], units: SelectionUnit[], includeQueued = false,
): SelectionUnit[] => {
  if (!runnable(row, includeQueued)) return [];
  if (row.task?.planning_metadata?.artifact_type === 'propagation_update') return units.filter((unit) => unit.taskId === row.id);
  const pageId = pageAncestor(row, rows);

  return pageId ? units.filter((unit) => unit.pageId === pageId)
    : units.filter((unit) => unit.stage === rowStage(row));
};

const isSelected = (unit: SelectionUnit, record: SelectionChoices): boolean => (
  record[unit.key] ?? record[`stage:${unit.stage}`] ?? true
);

const selectionKey = (scope: PlanningActionScope, rows: PlanningActionTaskRow[]): string => {
  const stages = planningActionStages(scope);
  const propagationId = planningPropagationId(scope);

  if (propagationId) return JSON.stringify([scope.projectId, 'artifact_updates', propagationId]);
  const runId = rows.find((row) => row.task?.planning_metadata?.run_id)?.task?.planning_metadata?.run_id ?? 'proposed';

  return JSON.stringify([
    scope.projectId, runId === 'proposed' ? scope.entryId ?? '' : '', runId,
    stages.length ? stages : Array.from(new Set(rows.map(rowStage))),
  ]);
};

const subscribe = (key: string, listener: () => void): (() => void) => {
  const current = listeners.get(key) ?? new Set<() => void>();

  current.add(listener);
  listeners.set(key, current);

  return () => {
    current.delete(listener);
    if (!current.size) listeners.delete(key);
  };
};

const useSelectionRecord = (key: string, proposedKey?: string): SelectionRecord => {
  const record = useSyncExternalStore(
    useCallback((listener) => subscribe(key, listener), [key]),
    useCallback(() => records.get(key) ?? records.get(proposedKey ?? '') ?? EMPTY_RECORD, [key, proposedKey]),
    () => EMPTY_RECORD,
  );

  useEffect(() => {
    const proposed = records.get(proposedKey ?? '');

    if (proposed && !records.has(key)) writeRecord(key, proposed);
  }, [key, proposedKey]);

  return record;
};

const prepareInitialPreview = (
  projectId: string, entryId: string, phase: string | null | undefined, buttonKey: string,
): Promise<unknown> => {
  const sourceKey = JSON.stringify([projectId, entryId, buttonKey]);
  const existing = previews.get(sourceKey);

  if (existing) return existing;
  const request = projectService.preparePlanningTaskPreview(projectId, entryId, phase || 'agent_suggestion', buttonKey)
    .catch((error) => { previews.delete(sourceKey); throw error; });

  previews.set(sourceKey, request);

  return request;
};

const useInitialPlanningPreview = (
  scope: PlanningActionScope, needed: boolean, refetch: (() => unknown) | undefined,
): void => {
  const { projectId, entryId, phase } = scope;
  const buttonKey = scope.buttons?.find((button) => button.action === 'trigger_agents')?.key ?? 'start';

  useEffect(() => {
    if (!needed || !projectId || !entryId) return undefined;
    let mounted = true;

    void prepareInitialPreview(projectId, entryId, phase, buttonKey).then(() => {
      if (mounted) refetch?.();
    }).catch(() => undefined);

    return () => { mounted = false; };
  }, [needed, projectId, entryId, phase, buttonKey, refetch]);
};

// eslint-disable-next-line max-statements
const selectionLabel = (base: string, selected: SelectionUnit[], units: SelectionUnit[], active: boolean): string => {
  if (!active) return base;
  if (!selected.length) return 'Select Tasks to Continue';
  if (selected.every((unit) => unit.stage === 'artifact_updates')) {
    return `${/^retry\b/i.test(base) ? 'Retry' : 'Run'} ${selected.length} Update${selected.length === 1 ? '' : 's'}`;
  }
  if (selected.length === units.length) return base;
  const pagesOnly = selected.every((unit) => Boolean(unit.pageId));
  const verb = /^retry\b/i.test(base) ? 'Retry' : pagesOnly ? 'Generate' : 'Run';
  const noun = pagesOnly ? 'Page' : 'Task';

  return `${verb} ${selected.length} Selected ${noun}${selected.length === 1 ? '' : 's'}`;
};

const selectableRows = (
  rows: PlanningActionTaskRow[], units: SelectionUnit[], record: SelectionChoices,
  active: boolean, includeQueued = false,
): PlanningSelectableTaskRow[] => rows.map((row) => {
  const related = unitsForRow(row, rows, units, includeQueued);
  const count = related.filter((unit) => isSelected(unit, record)).length;
  const dependency = related.length === 1 && related[0].rowId !== row.id
    && row.task?.planning_metadata?.artifact_type !== 'stage';
  const owner = dependency ? rows.find((item) => item.id === related[0].rowId)?.title : undefined;

  return {
    ...row, selectable: active && related.length > 0, checked: related.length > 0 && count === related.length,
    indeterminate: count > 0 && count < related.length,
    selectionHint: owner ? `Required with ${owner}; select or clear the whole ${related[0].pageId ? 'page' : 'stage'}.` : undefined,
  };
});

const changeSelection = (
  key: string, row: PlanningActionTaskRow, rows: PlanningActionTaskRow[], units: SelectionUnit[], checked: boolean,
  initialStart = false,
): void => {
  const related = unitsForRow(row, rows, units);
  const previous = records.get(key) ?? EMPTY_RECORD;
  const next = { ...previous.choices };

  if (!related.length) return;
  related.forEach((unit) => {
    next[unit.key] = checked;
    if (initialStart && unit.stage === 'user_flows' && checked) next['stage:srd'] = true;
    if (initialStart && unit.stage === 'srd' && !checked) next['stage:user_flows'] = false;
  });
  if (!row.task || row.task.planning_metadata?.artifact_type === 'stage') next[`stage:${rowStage(row)}`] = checked;
  writeRecord(key, { ...previous, choices: next });
};

const unitIsPrepared = (unit: SelectionUnit, payload: PlanningActionSelectionInput): boolean => (
  unit.taskId || unit.pageId ? payload.task_ids.includes((unit.taskId ?? unit.pageId)!)
    : payload.stages.includes(unit.stage)
);
const rowSignature = (row: PlanningActionTaskRow): string => JSON.stringify([
  row.task?.status, row.task?.started_at, row.task?.completed_at, row.task?.updated_at,
]);
const preparedOwnerRows = (
  rows: PlanningActionTaskRow[], payload: PlanningActionSelectionInput,
): PlanningActionTaskRow[] => {
  const pageStages = rows.filter((row) => payload.task_ids.includes(row.id)).map(rowStage);

  return rows.filter((row) => payload.task_ids.includes(row.id)
    || (row.task?.planning_metadata?.artifact_type === 'stage'
      && [...payload.stages, ...pageStages].includes(rowStage(row))));
};
const executionStarted = (prepared: PreparedSelection, rows: PlanningActionTaskRow[], tasks: Task[]): boolean => {
  const retry = tasks.find((task) => task.planning_metadata?.artifact_type === 'retry_attempt'
    && task.planning_metadata?.action_invocation_id === prepared.id);

  // Preparing a retry updates its failed parent without starting execution.
  // The separate attempt is authoritative, including a fast repeated failure
  // whose running event was missed by the client.
  if (retry) return retry.status !== 'backlog';

  return preparedOwnerRows(rows, prepared.payload).some((row) => {
    const status = row.task?.status;

    if (status === 'ai_in_progress' || status === 'human_in_progress') return true;

    return Boolean(status && ['done', 'error', 'cancelled', 'invalid'].includes(status)
      && rowSignature(row) !== prepared.baseline[row.id]);
  });
};
const asObject = (value: unknown): Record<string, unknown> | undefined => (
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : undefined
);
const asStrings = (value: unknown): string[] => (
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
);

const awaitingAdmission = (row: PlanningActionTaskRow): boolean => (
  row.task?.status === 'queued' || (row.task?.status === 'backlog'
    && row.task.planning_metadata?.dispatch_admission_pending === true)
);

// Credit-blocked preparations remain backlog until dispatch is admitted.
// Recover the original selection instead of creating another invocation.
// eslint-disable-next-line max-statements
const preparedFromTask = (
  row: PlanningActionTaskRow, rows: PlanningActionTaskRow[], retired: string[],
): PreparedSelection | undefined => {
  const metadata = row.task?.planning_metadata;
  const id = metadata?.active_selection_invocation_id;

  if (typeof id !== 'string' || retired.includes(id) || !awaitingAdmission(row)) return undefined;
  const saved = asObject(asObject(metadata?.action_selections)?.[id]);
  const requested = asObject(saved?.requested);
  const payload = { task_ids: asStrings(requested?.task_ids), stages: asStrings(requested?.stages) };
  const stages = asStrings(saved?.stages);

  const propagation = metadata?.artifact_type === 'propagation_update';

  if ((!stages.length && !propagation) || (!payload.task_ids.length && !payload.stages.length)) return undefined;
  const owners = preparedOwnerRows(rows, payload);
  const queuedStages = owners.filter((owner) => owner.task?.planning_metadata?.artifact_type === 'stage');

  if (!propagation && stages.some((stage) => !queuedStages.some((owner) => rowStage(owner) === stage))) {
    return undefined;
  }
  if (propagation && (payload.stages.length || owners.some((owner) => (
    owner.task?.planning_metadata?.propagation_id !== metadata?.propagation_id
    || owner.task?.planning_metadata?.artifact_type !== 'propagation_update'
  )))) return undefined;
  if (owners.some((owner) => !awaitingAdmission(owner))) return undefined;
  if (payload.task_ids.some((id) => !owners.some((owner) => owner.id === id))) return undefined;

  return {
    id, payload, label: typeof saved?.displayed_label === 'string' ? saved.displayed_label : undefined,
    units: selectionUnits(rows, true),
    baseline: Object.fromEntries(owners.map((owner) => [owner.id, rowSignature(owner)])),
  };
};

const restoredPreparation = (rows: PlanningActionTaskRow[], retired: string[] = []): PreparedSelection | undefined => (
  rows.map((row) => preparedFromTask(row, rows, retired)).find(Boolean)
);

const writeRecord = (key: string, record: SelectionRecord): void => {
  records.set(key, record);
  listeners.get(key)?.forEach((listener) => listener());
};

const reconcilePreparation = (key: string, retired?: string, recovered?: PreparedSelection): void => {
  const current = records.get(key) ?? EMPTY_RECORD;

  if (retired && current.prepared?.id === retired) writeRecord(key, {
    ...current, choices: {}, prepared: undefined, retired: [...current.retired ?? [], retired],
  });
  else if (!current.prepared && recovered && !current.retired?.includes(recovered.id)) {
    writeRecord(key, { ...current, prepared: recovered });
  }
};

const usePreparedSelection = (key: string, rows: PlanningActionTaskRow[], record: SelectionRecord, tasks: Task[]) => {
  const retired = record.prepared && executionStarted(record.prepared, rows, tasks) ? record.prepared.id : undefined;
  const recoveredKey = JSON.stringify(restoredPreparation(rows, [
    ...(record.retired ?? []), ...(retired ? [retired] : []),
  ]));
  const recovered = useMemo<PreparedSelection | undefined>(
    () => recoveredKey ? JSON.parse(recoveredKey) : undefined, [recoveredKey],
  );
  const prepared = retired ? undefined : record.prepared ?? recovered;

  useEffect(() => reconcilePreparation(key, retired, recovered), [key, recovered, retired]);

  return prepared;
};

const preparedChoices = (prepared: PreparedSelection, units: SelectionUnit[]): SelectionChoices => (
  Object.fromEntries(units.map((unit) => [unit.key, unitIsPrepared(unit, prepared.payload)]))
);

/**
 * A queued stage normally means its action is already in flight. When an
 * earlier stage in the same action has failed, however, later queued stages
 * are stranded behind that failure and must not make the Retry gate inert.
 * Actual worker execution remains authoritative even if another row failed.
 */
const selectionBlockedByRunningWork = (rows: PlanningActionTaskRow[]): boolean => {
  if (rows.some((row) => row.task && EXECUTING.has(row.task.status))) return true;

  const stages = Array.from(new Set(rows.map(rowStage).filter(Boolean)));
  const stageIndexes = new Map(stages.map((stage, index) => [stage, index]));
  const failedIndexes = rows.filter((row) => ['error', 'cancelled'].includes(row.task?.status ?? ''))
    .map((row) => stageIndexes.get(rowStage(row)))
    .filter((index): index is number => index !== undefined);
  const queuedIndexes = rows.filter((row) => row.task?.status === 'queued')
    .map((row) => stageIndexes.get(rowStage(row)))
    .filter((index): index is number => index !== undefined);

  if (!queuedIndexes.length) return false;

  return !queuedIndexes.every((queuedIndex) => failedIndexes.some((failedIndex) => failedIndex < queuedIndex));
};

const markSelectionPrepared = (
  key: string, id: string, payload: PlanningActionSelectionInput, units: SelectionUnit[],
  rows: PlanningActionTaskRow[], label?: string,
): void => {
  const previous = records.get(key) ?? EMPTY_RECORD;
  const prepared = {
    id, payload, units, label,
    baseline: Object.fromEntries(preparedOwnerRows(rows, payload).map((row) => [row.id, rowSignature(row)])),
  };

  writeRecord(key, { ...previous, prepared });
};

/** One shared selection for a review gate, whether read by its checklist or its action button. */
// eslint-disable-next-line max-statements
export const usePlanningActionSelection = (scope: PlanningActionScope) => {
  const { tasks, isLoading, refetch } = usePlanningTasks(scope.projectId);
  const rows = planningActionTaskRows(tasks, scope);
  const key = selectionKey(scope, rows);
  const initialStart = initialStartGate(scope);
  const record = useSelectionRecord(key, initialStart ? selectionKey(scope, []) : undefined);
  const prepared = usePreparedSelection(key, rows, record, tasks);
  const gate = resumeGate(scope);
  const running = selectionBlockedByRunningWork(rows);
  const active = gate && (Boolean(prepared) || !running);
  const units = selectionUnits(rows, Boolean(prepared));
  const choices = prepared ? preparedChoices(prepared, units) : record.choices;
  const selected = active ? units.filter((unit) => isSelected(unit, choices)) : [];

  useInitialPlanningPreview(scope, active && initialStart && !isLoading && !rows.some((row) => row.task), refetch);
  const payloadKey = JSON.stringify({
    task_ids: selected.flatMap((unit) => unit.taskId || unit.pageId ? [(unit.taskId ?? unit.pageId)!] : []),
    stages: selected.filter((unit) => !unit.pageId && !unit.taskId).map((unit) => unit.stage),
  });
  const normalPayload = useMemo<PlanningActionSelectionInput>(() => JSON.parse(payloadKey), [payloadKey]);
  const payload = prepared?.payload ?? normalPayload;
  const toggle = useCallback((rowId: string, checked: boolean) => {
    const row = rows.find((item) => item.id === rowId);

    if (active && !prepared && row) changeSelection(key, row, rows, units, checked, initialStart);
  }, [active, initialStart, key, prepared, rows, units]);
  const markPrepared = useCallback((id: string, label?: string) => {
    if (active && !prepared && id) markSelectionPrepared(key, id, payload, units, rows, label);
  }, [active, key, payload, prepared, rows, units]);
  const labelFor = (baseLabel: string): string => {
    if (!active) return baseLabel;
    if (prepared) return prepared.label ?? selectionLabel(
      baseLabel, prepared.units.filter((unit) => unitIsPrepared(unit, prepared.payload)), prepared.units, true,
    );

    return selectionLabel(baseLabel, selected, units, active);
  };

  return {
    tasks, rows: selectableRows(rows, units, choices, active && !prepared, Boolean(prepared)), isLoading, active,
    selectedTaskIds: payload.task_ids, selectedStages: payload.stages,
    selection: active ? payload : undefined,
    disabled: gate && (isLoading || (!prepared && (running || !selected.length))),
    unitCount: prepared?.units.length ?? units.length,
    actionIdentity: `planning:${key}`, preparedInvocationId: active ? prepared?.id : undefined,
    labelFor, toggle, markPrepared,
  };
};

export const resetPlanningActionSelectionForTests = (): void => {
  records.clear();
  listeners.clear();
  previews.clear();
};
